import React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'
import Switch from '@/v2/components/common/Switch'
import Typography from '@/v2/components/common/Typography'
import { useUpdateConversation } from '@/v2/hooks/messages/useConversations'

// conversations.reschedule_enabled, default true. The whole contract with the
// backend sits in these three lines: nothing below reads the payload by hand.
//
// A conversation that does not carry the key reads as on — the column's own
// default, and what the list endpoint serves, which does not send the field.
export const FIELD = 'rescheduleEnabled'
export const readsAsOn = conversation => conversation[FIELD] !== false
export const payloadFor = on => ({ [FIELD]: on })

const Wrapper = styled.div`
  margin-top: 22px;
`

const Note = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  line-height: 16px;
  color: #666;
`

const RescheduleSwitch = ({ conversation = {} }) => {
  const queryClient = useQueryClient()
  const { mutateAsync } = useUpdateConversation(conversation.id)

  const on = readsAsOn(conversation)
  const queryKey = ['Conversation', `${conversation.id}`]

  // Flip the cached conversation first so the switch moves under the finger,
  // then write the server's answer over it. That answer carries the recomputed
  // rescheduleLink next to the flag, which is what the "Send reschedule link"
  // button of this same page reads — merging the payload keeps the two halves
  // of the screen agreeing without a refetch.
  //
  // requestMiddleware resolves an HTTP error as { error } instead of throwing
  // and has already raised the snackbar, so both failure exits here only have to
  // put the switch back where the server still has it.
  const onChange = async event => {
    const next = event.target.checked
    const previous = queryClient.getQueryData(queryKey)

    if (previous) {
      queryClient.setQueryData(queryKey, { ...previous, ...payloadFor(next) })
    }

    const rollback = () => {
      if (previous) queryClient.setQueryData(queryKey, previous)
    }

    try {
      const result = await mutateAsync(payloadFor(next))

      if (result?.error) return rollback()

      // The update payload has no messages and no unreadMessagesCount; a shallow
      // merge leaves the ones the show endpoint already put there.
      if (result?.id) {
        queryClient.setQueryData(queryKey, current =>
          current ? { ...current, ...result } : current
        )
      }
    } catch (error) {
      rollback()
    }
  }

  // The clinic switch in Settings decides whether the feature exists at all, and
  // the payload tells its off apart from this one's: `disabled` is the master,
  // `conversation_disabled` is this switch. With the master off there is no
  // rescheduling to silence in any thread, so the control has no business on the
  // screen — the same answer the "Send reschedule link" button gives, and the
  // two sit close enough that one vanishing while the other explained itself
  // read as a defect. The stored preference is untouched and comes back with the
  // switch on the day the clinic turns rescheduling on.
  if (conversation.rescheduleLink?.reason === 'disabled') return null

  return (
    <Wrapper>
      <Typography fontSize="14px" lineHeight="18px" variant="h2">
        Rescheduling in this conversation
      </Typography>

      <Switch label={on ? 'On' : 'Off'} checked={on} onChange={onChange} />

      <Note>
        With this off, a patient who asks to reschedule here goes to your team
        instead of being answered with a link, and Send reschedule link stops
        working in this thread. A link already sent keeps working.
      </Note>
    </Wrapper>
  )
}

export default RescheduleSwitch
